import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { X, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { ProjectBasicsStep } from './job-steps/ProjectBasicsStep';
import { ProjectDetailsStep } from './job-steps/ProjectDetailsStep';
import { LocationBudgetStep } from './job-steps/LocationBudgetStep';
import { ContactPreferencesStep } from './job-steps/ContactPreferencesStep';
import { ReviewSubmitStep } from './job-steps/ReviewSubmitStep';
import { inquiryService } from '../services/inquiryService';
import { useAuth } from '../hooks/useAuth';
import { uploadMultipleToWasabi } from '../utils/wasabiUpload';
import { authService } from '../services/authService';
import { useFeatureFlag } from '../context/FeatureFlagContext';
import customerAddressService from '../services/customerAddressService';

export const PostJobModal = ({ isOpen, onClose, onSuccess, draftToEdit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const { user, isAuthenticated } = useAuth();
  const isAutoRegistrationEnabled = useFeatureFlag('auto_registration_enabled');
  
  // Initialize form data with user information and saved preferences
  const initializeFormData = () => {
    // Get saved contact preferences from localStorage
    const savedContactData = localStorage.getItem('postjob_contact_preferences');
    let contactPreferences = {};
    
    try {
      contactPreferences = savedContactData ? JSON.parse(savedContactData) : {};
    } catch (error) {
      console.error('Error parsing saved contact preferences:', error);
    }

    // Pre-fill with user data if available
    const userContactData = {};
    if (user) {
      // Extract name parts if user has a full name
      if (user.name) {
        const nameParts = user.name.trim().split(' ');
        userContactData.contact_first_name = nameParts[0] || '';
        userContactData.contact_last_name = nameParts.slice(1).join(' ') || '';
      }
      
      // Use user email if available
      if (user.email) {
        userContactData.contact_email = user.email;
      }
      
      // Use user phone if available
      if (user.phone) {
        userContactData.contact_phone = user.phone;
      }
    }

    // For authenticated users, prioritize their account info over localStorage
    // For non-authenticated users, use localStorage preferences as primary
    const getContactValue = (field) => {
      if (user) {
        // For authenticated users: prefer user data, fall back to localStorage
        return userContactData[field] || contactPreferences[field] || '';
      } else {
        // For non-authenticated users: use localStorage as primary
        return contactPreferences[field] || '';
      }
    };

    return {
      // Step 1: Project Basics
      service_id: draftToEdit?.service?.id?.toString() || draftToEdit?.service_id?.toString() || draftToEdit?.service?.toString() || '',
      details: draftToEdit?.description || draftToEdit?.details || '',

      // Step 2: Project Details
      photos: [], // Re-populating wasabi images might need extra logic but we leave empty for now
      location: draftToEdit?.location || '',


      // Step 3: Location & Budget
      zipcode: draftToEdit?.postcode || draftToEdit?.zipcode || '',
      fullAddress: draftToEdit?.street_address || '',
      street_address: draftToEdit?.street_address || '',
      apt_unit: '',
      city: draftToEdit?.city || '',
      state: draftToEdit?.state || '',
      country: draftToEdit?.country || '',
      latitude: draftToEdit?.latitude || null,
      longitude: draftToEdit?.longitude || null,
      budget: draftToEdit?.budget_raw || draftToEdit?.budget || '',
      budgetType: 'range', // 'range' or 'quotes'
      
      // Step 4: Contact & Preferences - Pre-filled with user data (prioritized for logged-in users)
      contact_first_name: getContactValue('contact_first_name'),
      contact_last_name: getContactValue('contact_last_name'),
      contact_email: getContactValue('contact_email'),
      contact_phone: getContactValue('contact_phone'),
      contactMethod: contactPreferences.contactMethod || 'both',
      notifications: {
        bidReceived: contactPreferences.notifications?.bidReceived ?? true,
        messages: contactPreferences.notifications?.messages ?? true,
        updates: contactPreferences.notifications?.updates ?? true
      },
      
      // Step 5: Review & Submit
      agreeToTerms: false,
      email_updates: true,

      // Auto-registration
      createAccount: false,
      password: ''
    };
  };

  const [formData, setFormData] = useState(initializeFormData());

  // Re-initialize form when user changes (login/logout) or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(initializeFormData());
      setIsSubmitted(false);
      setCurrentStep(1);
      setSubmitError('');
      // Fetch saved addresses for authenticated customers
      if (isAuthenticated && user?.role === 'customer') {
        customerAddressService.getAddresses().then(r => {
          if (r.success) setSavedAddresses(r.data);
        });
      }
    }
  }, [user, isOpen, draftToEdit]);

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  const stepTitles = [
    'Describe Your Project',
    'Upload Photos',
    'Location & Budget',
    'Contact & Preferences',
    'Review & Submit'
  ];

  const stepComponents = {
    1: ProjectBasicsStep,
    2: ProjectDetailsStep,
    3: LocationBudgetStep,
    4: ContactPreferencesStep,
    5: ReviewSubmitStep
  };

  const updateFormData = (stepData) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        ...stepData
      };

      // Save contact preferences to localStorage when contact fields change
      const contactFields = [
        'contact_first_name', 
        'contact_last_name', 
        'contact_email', 
        'contact_phone', 
        'contactMethod',
        'notifications'
      ];

      const hasContactFieldChanges = Object.keys(stepData).some(key => contactFields.includes(key));
      
      if (hasContactFieldChanges) {
        try {
          const contactPreferences = {
            contact_first_name: newData.contact_first_name,
            contact_last_name: newData.contact_last_name,
            contact_email: newData.contact_email,
            contact_phone: newData.contact_phone,
            contactMethod: newData.contactMethod,
            notifications: newData.notifications
          };
          
          localStorage.setItem('postjob_contact_preferences', JSON.stringify(contactPreferences));
        } catch (error) {
          console.error('Error saving contact preferences:', error);
        }
      }

      return newData;
    });

    // Clear validation errors when user makes changes
    if (Object.keys(validationErrors).length > 0) {
      const updatedErrors = { ...validationErrors };
      Object.keys(stepData).forEach(key => {
        delete updatedErrors[key];
      });
      setValidationErrors(updatedErrors);
    }
  };

  const handleNext = () => {
    // Validate current step before proceeding
    const validation = validateCurrentStep();
    if (validation.isValid && currentStep < totalSteps) {
      setValidationErrors({});
      setCurrentStep(prev => prev + 1);
      // Scroll to top of modal content when moving to next step
      setTimeout(() => {
        const modalContent = document.querySelector('[data-modal-content="true"]');
        if (modalContent) {
          modalContent.scrollTop = 0;
        }
      }, 100);
    } else {
      setValidationErrors(validation.errors);
      // Scroll the first validation error into view so the user sees what's wrong
      setTimeout(() => {
        const modalContent = document.querySelector('[data-modal-content="true"]');
        if (!modalContent) return;
        const firstError = modalContent.querySelector('p.text-red-600');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          modalContent.scrollTop = 0;
        }
      }, 50);
    }
  };

  const validateCurrentStep = () => {
    const errors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.service_id) {
          errors.service_id = 'Please select a service';
        }
        if (!formData.details || formData.details.trim().length < 10) {
          errors.details = 'Please provide a detailed description (at least 10 characters)';
        }
        break;

      case 2:
        // Photos are optional — no validation
        break;

      case 3:
        if (!formData.zipcode || !formData.zipcode.trim()) {
          errors.zipcode = 'Please enter your zip code';
        } else if (!inquiryService.isValidZipCode(formData.zipcode)) {
          errors.zipcode = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)';
        }
        if (!formData.budget) {
          errors.budget = 'Please select a budget range';
        }
        break;
        
      case 4:
        if (!formData.contact_first_name || !formData.contact_first_name.trim()) {
          errors.contact_first_name = 'Please enter your first name';
        }
        if (!formData.contact_last_name || !formData.contact_last_name.trim()) {
          errors.contact_last_name = 'Please enter your last name';
        }
        if (!formData.contact_email || !formData.contact_email.trim()) {
          errors.contact_email = 'Please enter your email address';
        } else if (!inquiryService.isValidEmail(formData.contact_email)) {
          errors.contact_email = 'Please enter a valid email address';
        }
        if (formData.contact_phone && !inquiryService.isValidPhone(formData.contact_phone)) {
          errors.contact_phone = 'Please enter a valid phone number';
        }
        
        // Validate password if creating account
        if (formData.createAccount && !isAuthenticated && isAutoRegistrationEnabled) {
          if (!formData.password) {
            errors.password = 'Password is required to create an account';
          } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
          }
        }
        break;
        
      case 5:
        if (!formData.agreeToTerms) {
          errors.agreeToTerms = 'Please agree to the terms and conditions';
        }
        break;
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleStepClick = (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);

  const handleSaveDraft = async () => {
    if (!isAuthenticated) {
      setSubmitError('You must be signed in to save a draft.');
      return;
    }

    setIsSavingDraft(true);
    setSubmitError('');

    try {
      // Upload photos directly to Wasabi if any
      let imageSignedIds = [];
      if (formData.photos && formData.photos.length > 0) {
        const token = authService.getToken();
        if (token) {
          const photoFiles = formData.photos.map(photo => photo.file).filter(Boolean);
          if (photoFiles.length > 0) {
            imageSignedIds = await uploadMultipleToWasabi(photoFiles, token);
          }
        }
      }

      // Prepare payload (no strict validations for drafts)
      const inquiryData = {
        status: 'draft',
        service_id: formData.service_id ? parseInt(formData.service_id) : undefined,
        details: formData.details || undefined,
        street_address: (() => {
          const base = formData.street_address || formData.fullAddress;
          if (!base) return undefined;
          return formData.apt_unit ? `${base}, Apt ${formData.apt_unit}` : base;
        })(),
        city: formData.city || undefined,
        state: formData.state || undefined,
        postcode: formData.zipcode || undefined,
        country: formData.country || undefined,
        latitude: formData.latitude || undefined,
        longitude: formData.longitude || undefined,
        contact_first_name: formData.contact_first_name || undefined,
        contact_last_name: formData.contact_last_name || undefined,
        contact_email: formData.contact_email || undefined,
        contact_phone: formData.contact_phone || undefined,
        budget: formData.budget || undefined,
      };

      if (imageSignedIds.length > 0) {
        inquiryData.images = imageSignedIds;
      }

      let result;
      if (draftToEdit?.id) {
        result = await inquiryService.updateInquiry(draftToEdit.id, inquiryData);
      } else {
        result = await inquiryService.submitInquiry(inquiryData);
      }

      if (result.success) {
        if (onSuccess) onSuccess(result.data); // Reload dashboard
        onClose(); // Auto close if drafted 
        // We'll reset state there
      } else {
        setSubmitError(result.message);
      }
    } catch (error) {
      console.error('Save draft error:', error);
      setSubmitError('An unexpected error occurred saving the draft.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Remove blocking check for authentication if user is creating an account
    if (!isAuthenticated && (!formData.createAccount || !isAutoRegistrationEnabled)) {
      setSubmitError('You must be signed in to post a project, or choose to create an account.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Handle auto-registration
      let authToken = authService.getToken();
      
      if (formData.createAccount && !isAuthenticated && isAutoRegistrationEnabled) {
        const registerResult = await authService.register({
          name: `${formData.contact_first_name} ${formData.contact_last_name}`.trim(),
          email: formData.contact_email,
          password: formData.password,
          role: 'customer',
          phone: formData.contact_phone
        });

        if (!registerResult.success) {
          setSubmitError(registerResult.message || 'Registration failed. Please try again.');
          setIsSubmitting(false);
          return;
        }
        
        // Get the new token
        authToken = authService.getToken();
      }

      // Prepare inquiry data for API
      const inquiryData = {
        // Service Selection (REQUIRED)
        service_id: parseInt(formData.service_id),

        // Project Details (REQUIRED)
        details: formData.details,

        // New Address Fields from Places API (ALL REQUIRED)
        street_address: (() => {
          const base = formData.street_address || formData.fullAddress;
          return formData.apt_unit && base ? `${base}, Apt ${formData.apt_unit}` : base;
        })(),
        city: formData.city,
        state: formData.state,
        postcode: formData.zipcode,
        country: formData.country,
        latitude: formData.latitude,
        longitude: formData.longitude,

        // Contact Information (REQUIRED)
        contact_first_name: formData.contact_first_name,
        contact_last_name: formData.contact_last_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone || undefined,

        // Budget (REQUIRED - choose one)
        budget: formData.budget,

        // Publish Status
        status: 'pending',

        // Email notifications for new bids
        email_updates: formData.email_updates ?? false,

        // Saved address reference (optional)
        ...(formData.saved_address_id && { saved_address_id: formData.saved_address_id }),
      };

      // Upload photos directly to Wasabi if any
      let imageSignedIds = [];
      if (formData.photos && formData.photos.length > 0) {
        // Use the token we got (either existing or from new registration)
        const token = authToken;

        if (!token) {
          setSubmitError('Authentication token not found. Please sign in again.');
          setIsSubmitting(false);
          return;
        }

        try {
          const photoFiles = formData.photos.map(photo => photo.file).filter(Boolean);
          if (photoFiles.length > 0) {
            imageSignedIds = await uploadMultipleToWasabi(photoFiles, token);
          }
        } catch (uploadError) {
          console.error('Photo upload error:', uploadError);
          setSubmitError('Failed to upload photos. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // Add images to inquiry data
      if (imageSignedIds.length > 0) {
        inquiryData.images = imageSignedIds;
      }

      // Submit or update inquiry
      let result;
      if (draftToEdit?.id) {
        result = await inquiryService.updateInquiry(draftToEdit.id, inquiryData);
      } else {
        result = await inquiryService.submitInquiry(inquiryData);
      }
      
      if (result.success) {
        setIsSubmitted(true);
        if (onSuccess) onSuccess(result.data);

        // Fire-and-forget: save address to profile if requested
        if (formData.save_address_to_profile && formData.address_label && formData.street_address) {
          customerAddressService.createAddress({
            label: formData.address_label,
            street_address: formData.street_address,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            postcode: formData.zipcode,
            latitude: formData.latitude,
            longitude: formData.longitude,
            formatted_address: formData.fullAddress,
            is_default: false,
          }).catch(() => {}); // silently ignore errors
        }

        // Auto close after 8 seconds
        setTimeout(() => {
          resetForm();
          onClose();
        }, 8000);
      } else {
        setSubmitError(result.message);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setCurrentStep(1);
    setSubmitError('');
    setFormData({
      service_id: '',
      details: '',
      photos: [],
      location: '',
      zipcode: '',
      fullAddress: '',
      street_address: '',
      apt_unit: '',
      city: '',
      state: '',
      country: '',
      latitude: null,
      longitude: null,
      budget: '',
      budgetType: 'range',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      contact_phone: '',
      contactMethod: 'both',
      notifications: {
        bidReceived: true,
        messages: true,
        updates: true
      },
      agreeToTerms: false,
      email_updates: true,
      createAccount: false,
      password: ''
    });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setTimeout(() => {
      resetForm();
    }, 300);
  };

  // Success state
  if (isSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md mx-auto p-8 bg-white rounded-2xl shadow-2xl border-0">
          <DialogTitle className="sr-only">Success</DialogTitle>
          <div className="text-center">
            <div className="bg-emerald-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Project Posted Successfully!
            </h2>
            <p className="text-gray-600 mb-6">
              Your project has been posted and service providers will start bidding soon. 
              You'll receive notifications when bids come in.
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const CurrentStepComponent = stepComponents[currentStep];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent hideClose className="p-0 bg-white border-0 flex flex-col overflow-hidden z-50 max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl">
        <DialogTitle className="sr-only">Post Your Project</DialogTitle>
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Post Your Project
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isSubmitting}
                className="text-gray-600 border-gray-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"
              >
                {isSavingDraft ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                ) : (
                  <span>Save Draft</span>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="rounded-full w-8 h-8 p-0 hover:bg-gray-100"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          
          {/* Progress Indicator */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Step {currentStep} of {totalSteps}: {stepTitles[currentStep - 1]}
              </span>
              <span className="text-emerald-600 font-medium">
                {Math.round(progress)}% Complete
              </span>
            </div>
            
            <Progress value={progress} className="h-2" />
            
            {/* Step Pills */}
            <div className="flex items-center justify-between">
              {stepTitles.map((title, index) => {
                const stepNumber = index + 1;
                const isActive = stepNumber === currentStep;
                const isCompleted = stepNumber < currentStep;
                const isClickable = stepNumber <= currentStep;
                
                return (
                  <button
                    key={stepNumber}
                    onClick={() => isClickable && handleStepClick(stepNumber)}
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-all duration-200 ${
                      isCompleted
                        ? 'bg-emerald-500 text-white'
                        : isActive
                        ? 'bg-emerald-500 text-white ring-4 ring-emerald-200'
                        : 'bg-gray-200 text-gray-500'
                    } ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'}`}
                  >
                    {isCompleted ? (
                      <CheckCircle size={14} />
                    ) : (
                      stepNumber
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 overflow-y-auto flex-1" data-modal-content="true">
          {/* Error Display */}
          {submitError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{submitError}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setSubmitError('')}
                    className="inline-flex bg-red-50 rounded-md p-1.5 text-red-400 hover:bg-red-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
            validationErrors={validationErrors}
            isAuthenticated={isAuthenticated}
            onEditStep={setCurrentStep}
            savedAddresses={savedAddresses}
            onSaveAddress={isAuthenticated && user?.role === 'customer'}
          />
        </div>

        {/* Helper message on final step when terms aren't accepted */}
        {currentStep === totalSteps && !formData.agreeToTerms && (
          <div className="bg-amber-50 border-t border-amber-200 px-6 py-2.5 text-center text-sm font-medium text-amber-800 flex-shrink-0">
            Please review and accept the terms below to post your project.
          </div>
        )}

        {/* Footer Navigation */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSavingDraft || isSubmitting}
            className="flex items-center space-x-2 disabled:opacity-50 visible"
          >
            <ArrowLeft size={16} />
            <span>Previous</span>
          </Button>
          
          <div className="text-sm text-gray-500 font-medium">
            Step {currentStep} of {totalSteps}
          </div>
          
          {currentStep < totalSteps ? (
            <div className="flex items-center space-x-2">
              {currentStep === 2 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  className="flex items-center"
                >
                  Skip
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center space-x-2 visible"
              >
                <span>Next</span>
                <ArrowRight size={16} />
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.agreeToTerms}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center space-x-2 disabled:opacity-50 visible"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <span>Post Project</span>
                  <CheckCircle size={16} />
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};